import React, { useMemo } from "react";

import { Box } from "@chakra-ui/react";
import { ReactComponent as FaceBookIcon } from "assets/icons/facebook-icon.svg";
import { InstagramIcon } from "assets/icons/instagram-icon";
import { ReactComponent as InternetIcon } from "assets/icons/internet-icon.svg";
import { NgFlagRounded } from "assets/icons/ng-flag-rounded";
import { ReactComponent as TwitterIcon } from "assets/icons/twitter-icon.svg";
import { useBusinessCompliance } from "contexts/business-compliance-context";
import { BusinessContentItem } from "features/business-compliance/selected-business/BusinessContentItem";
import { ComplianceStatus } from "features/business-compliance/selected-business/ComplianceStatus";
import { IBusinessContentItem } from "features/business-compliance/selected-business/types";
import { formatDateToDDMMYYTTSS } from "utils";
import {
  REGISTERED_BUSINESS_COMPANY_TYPE,
  UNREGISTERED_BUSINESS_COMPANY_TYPE,
} from "utils/constants";

import { IBusinessDetailsInformationProps } from "./types";

const CHARACTER_START = 1;
export const BusinessDetailsInformation = (
  props: IBusinessDetailsInformationProps
) => {
  const { business, isContentWrapperBelow745, isContentWrapperBelow600 } =
    props;
  const { kybVerificationResultsData } = useBusinessCompliance();

  const isVerified = useMemo(
    () =>
      kybVerificationResultsData?.ResultText?.toLowerCase()?.includes(
        "verified"
      ),
    [kybVerificationResultsData]
  );
  let businessType = "Default";
  if (
    business?.business?.companyData?.companyType?.toLowerCase() ===
    REGISTERED_BUSINESS_COMPANY_TYPE
  ) {
    businessType = "Registered";
  } else if (
    business?.business?.companyData?.companyType ===
    UNREGISTERED_BUSINESS_COMPANY_TYPE
  ) {
    businessType = "Unregistered";
  }

  const businessInfoList: IBusinessContentItem[] = useMemo(
    () => [
      {
        title:
          business?.business?.companyData?.companyType ===
          REGISTERED_BUSINESS_COMPANY_TYPE
            ? "Registered Business Name"
            : "Business/Brand Name",
        content:
          (business?.business?.companyData?.businessName ||
            business?.business?.companyData?.companyName) ??
          "-",
      },
      business?.business?.companyData?.companyType ===
      REGISTERED_BUSINESS_COMPANY_TYPE
        ? {
            title: "Registration Number",
            content: (
              <Box className="flex items-center gap-2">
                <p className="uppercase">
                  {business?.business?.companyData?.registrationNumber || "-"}
                </p>
                {isVerified && (
                  <ComplianceStatus
                    status="approved"
                    text="Verified"
                  />
                )}
              </Box>
            ),
          }
        : {
            title: "Owner's Full Name",
            content: (
              <p className="capitalize">
                {`${business?.business?.firstName} ${business?.business?.lastName}`}
              </p>
            ),
          },
      {
        title: "Business Owner Phone Number",
        content: (
          <Box className="flex items-center">
            <NgFlagRounded className="mr-1" />
            <p>
              +234 &nbsp;
              {business?.business?.phoneNumber.slice(CHARACTER_START)}
            </p>
          </Box>
        ),
      },
      {
        title: "Business Owner Email Address",
        content: <span className="lowercase">{business?.business?.email}</span>,
      },
      {
        title: "Business Type",
        content: (
          <ComplianceStatus
            status={
              business?.business?.companyData?.companyType?.toLowerCase() ===
              REGISTERED_BUSINESS_COMPANY_TYPE
                ? "approved"
                : "rejected"
            }
            text={businessType}
            customBgColor={`${
              business?.business?.companyData?.companyType?.toLowerCase() ===
              REGISTERED_BUSINESS_COMPANY_TYPE
                ? "#EFF8FF"
                : "#F2F4F7"
            }`}
            customColor={`${
              business?.business?.companyData?.companyType?.toLowerCase() ===
              REGISTERED_BUSINESS_COMPANY_TYPE
                ? "#175CD3"
                : "#344054"
            }`}
            className="w-fit"
          />
        ),
      },
      business?.business?.companyData?.companyType ===
      REGISTERED_BUSINESS_COMPANY_TYPE
        ? {
            title: "Registration Type",
            content: (
              <p className="uppercase">
                {business?.business?.companyData?.registrationType || "-"}
              </p>
            ),
          }
        : { title: "", content: "" },
      {
        title: (
          <Box className="flex items-center gap-1">
            <InternetIcon />
            <span>Website</span>
          </Box>
        ),
        content: (
          <>
            {business?.business?.companyData?.socials?.website ? (
              <a
                href={business?.business?.companyData?.socials?.website}
                target="_blank"
                className="lowercase text-sm leading-6 font-medium text-[#534CCD]"
              >
                {business?.business?.companyData?.socials?.website}
              </a>
            ) : (
              "-"
            )}
          </>
        ),
      },
      {
        title: (
          <Box className="flex items-center gap-1">
            <FaceBookIcon />
            <span>Facebook</span>
          </Box>
        ),
        content: (
          <>
            {business?.business?.companyData?.socials?.facebook ? (
              <a
                href={business?.business?.companyData?.socials?.facebook}
                target="_blank"
                className="lowercase text-sm leading-6 font-medium text-[#534CCD]"
              >
                {business?.business?.companyData?.socials?.facebook}
              </a>
            ) : (
              "-"
            )}
          </>
        ),
      },
      {
        title: (
          <Box className="flex items-center gap-1">
            <InstagramIcon className="" />
            <span>Instagram</span>
          </Box>
        ),
        content: (
          <>
            {business?.business?.companyData?.socials?.instagram ||
            business?.business?.companyData?.socials?.others ? (
              <a
                href={
                  business?.business?.companyData?.socials?.instagram ||
                  business?.business?.companyData?.socials?.others
                }
                target="_blank"
                className="lowercase text-sm leading-6 font-medium text-[#534CCD]"
              >
                {business?.business?.companyData?.socials?.instagram ||
                  business?.business?.companyData?.socials?.others}
              </a>
            ) : (
              "-"
            )}
          </>
        ),
      },
      {
        title: (
          <Box className="flex items-center gap-1">
            <TwitterIcon />
            <span>Twitter</span>
          </Box>
        ),
        content: (
          <>
            {business?.business?.companyData?.socials?.twitter ? (
              <a
                href={business?.business?.companyData?.socials?.twitter}
                target="_blank"
                className="lowercase text-sm leading-6 font-medium text-[#534CCD]"
              >
                {business?.business?.companyData?.socials?.twitter}
              </a>
            ) : (
              "-"
            )}
          </>
        ),
      },
      {
        title: "Industry Type",
        content: (
          <p className="uppercase">
            {business?.business?.companyData?.industry || "-"}
          </p>
        ),
      },
      {
        title: "Business Description",
        content: (
          <Box className="h-fit">
            {business?.business?.companyData?.companyDescription || ""}
          </Box>
        ),
      },
      {
        title: "Last Login Date",
        content: (
          <Box className="h-fit">
            {business?.business?.lastLogin
              ? formatDateToDDMMYYTTSS(business?.business?.lastLogin)
              : "-"}
          </Box>
        ),
      },
    ],
    [isContentWrapperBelow745, isContentWrapperBelow600, business, isVerified]
  );

  return (
    <Box>
      <Box
        css={{
          display: "flex",
          flexWrap: "wrap",
          columnGap: "32px",
          rowGap: "16px",
          justifyContent: "space-between",
        }}
      >
        {businessInfoList.map(({ title, content }, index) => (
          <BusinessContentItem
            key={index}
            title={title}
            content={content}
            isContentWrapperBelow745={isContentWrapperBelow745}
            isContentWrapperBelow600={isContentWrapperBelow600}
          />
        ))}
      </Box>
    </Box>
  );
};
